/**
 * AlertsPanel - Admin alerts (real backend data only)
 */


import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useMonitoring } from "@/hooks/useMonitoring";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { AlertCircle, AlertTriangle, CheckCircle, Clock, XCircle } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";

const levelIcon = (level: string) => {
    switch (level) {
        case "critical":
            return <XCircle className="h-4 w-4 text-red-600" />;
        case "warning":
            return <AlertTriangle className="h-4 w-4 text-orange-600" />;
        case "info":
            return <CheckCircle className="h-4 w-4 text-blue-600" />;
        default:
            return <AlertCircle className="h-4 w-4 text-gray-600" />;
    }
};

const levelBadge = (level: string) => {
    switch (level) {
        case "critical":
            return <Badge variant="destructive" className="text-xs">Critico</Badge>;
        case "warning":
            return <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs">Attenzione</Badge>;
        case "info":
            return <Badge variant="outline" className="text-xs">Info</Badge>;
        default:
            return <Badge variant="secondary" className="text-xs">Sconosciuto</Badge>;
    }
};

const _safeDate = (value: unknown): Date | null => {
    const d = new Date(String(value || ""));
    return Number.isNaN(d.getTime()) ? null : d;
};

export const AlertsPanel: React.FC = () => {
    const { alerts, isLoading, resolveAlert } = useMonitoring();
    const [actionLoading, setActionLoading] = useState<string | null>(null);

    const active = useMemo(() => alerts.filter((a) => !a.resolved), [alerts]);
    const counts = useMemo(() => {
        const critical = active.filter((a) => a.level === "critical").length;
        const warning = active.filter((a) => a.level === "warning").length;
        const info = active.filter((a) => a.level === "info").length;
        return { critical, warning, info, total: critical + warning + info };
    }, [active]);

    const sorted = useMemo(() => {
        const levelPriority: Record<string, number> = { critical: 3, warning: 2, info: 1 };
        return [...active].sort((a, b) => {
            const ld = (levelPriority[b.level] || 0) - (levelPriority[a.level] || 0);
            if (ld !== 0) return ld;
            const bt = _safeDate(b.timestamp)?.getTime() || 0;
            const at = _safeDate(a.timestamp)?.getTime() || 0;
            return bt - at;
        });
    }, [active]);

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4" />
                        Avvisi
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        {counts.critical > 0 && (
                            <Badge variant="destructive" className="text-xs">{counts.critical}</Badge>
                        )}
                        {counts.warning > 0 && (
                            <Badge className="bg-orange-100 text-orange-800 border-orange-200 text-xs">{counts.warning}</Badge>
                        )}
                        {counts.total === 0 && (
                            <Badge variant="outline" className="text-xs text-green-700 border-green-200">
                                Nessun allarme
                            </Badge>
                        )}
                    </div>
                </div>
            </CardHeader>

            <CardContent>
                {isLoading ? (
                    <div className="text-sm text-muted-foreground">Carico avvisi...</div>
                ) : sorted.length === 0 ? (
                    <div className="text-center py-8">
                        <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                        <h3 className="text-sm font-medium text-foreground mb-1">
                            Nessun avviso attivo
                        </h3>
                        <p className="text-xs text-muted-foreground">
                            Tutti i sistemi sono operativi
                        </p>
                    </div>
                ) : (
                    <ScrollArea className="h-80">
                        <div className="space-y-3">
                            {sorted.map((a, index) => (
                                <motion.div
                                    key={`${a.id}-${a.timestamp}`}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.03 }}
                                    className={`p-3 rounded-lg border ${
                                        a.level === "critical"
                                            ? "bg-red-50 border-red-200"
                                            : a.level === "warning"
                                              ? "bg-orange-50 border-orange-200"
                                              : "bg-blue-50 border-blue-200"
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-2 flex-1">
                                            {levelIcon(a.level)}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <h4 className="text-sm font-medium text-foreground truncate">
                                                        {a.title}
                                                    </h4>
                                                    {levelBadge(a.level)}
                                                </div>
                                                <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
                                                    {a.message}
                                                </p>
                                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                    <Clock className="h-3 w-3" />
                                                    <span>
                                                        {(() => {
                                                            const ts = _safeDate(a.timestamp);
                                                            return ts
                                                                ? formatDistanceToNow(ts, {
                                                                      locale: it,
                                                                      addSuffix: true,
                                                                  })
                                                                : "N/A";
                                                        })()}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <Button
                                            size="sm"
                                            variant="outline"
                                            disabled={actionLoading === a.id}
                                            onClick={async () => {
                                                setActionLoading(a.id);
                                                try {
                                                    const res = await resolveAlert(a.id);
                                                    if (!res.success && "error" in res) {
                                                        console.error(res.error);
                                                    }
                                                } finally {
                                                    setActionLoading(null);
                                                }
                                            }}
                                        >
                                            Conferma
                                        </Button>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </ScrollArea>
                )}
            </CardContent>
        </Card>
    );
};
